import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFitbitStatus } from '../hooks/useFitbitStatus';
import { useDeviceStatus } from '../hooks/useDeviceStatus';
import useDeviceConnect from '../hooks/useDeviceConnect';
import { healthConnectSupported } from '../utils/healthConnect';
import { withSearch } from '../utils/attribution';

// /start — the funnel's device entry (R4). A video says "connect your
// wearable at vegetablah.com/start"; this page sets the expectation (which
// devices, what we import, when the first observation shows) and gets the
// person to the OAuth screen in one tap. Public: signed out, every button goes
// to sign-up and comes straight back here; signed in, it connects.
//
// Copy discipline: what we do and when, never what a supplement does. The
// device list is honest about WHOOP (not switched on yet) and Android (works
// through Health Connect in the app, including Garmin and Samsung).

const DEVICES = [
  { key: 'fitbit', label: 'Fitbit', desc: 'Sleep, resting heart rate, HRV, SpO2, steps' },
  { key: 'oura', label: 'Oura Ring', desc: 'Sleep, HRV, readiness, SpO2' },
  { key: 'withings', label: 'Withings', desc: 'Scale, body composition, blood pressure' },
  { key: 'whoop', label: 'WHOOP', desc: 'Recovery, HRV, sleep' },
];

const STEPS = [
  {
    n: '1',
    title: 'Connect',
    body: 'One tap to your device’s own sign-in. Read-only access; disconnect any time and the data goes with it.',
  },
  {
    n: '2',
    title: 'We import your history',
    body: 'Up to a year of readings, usually in a few minutes. Home says “importing” until it lands.',
  },
  {
    n: '3',
    title: 'The first observations arrive',
    body: 'Once about three months of history is in, we can say what is unusual for you — in your own numbers, with a chart and how sure we are.',
  },
];

const Row = ({ label, desc, action }) => (
  <li className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-raised px-4 py-3">
    <div className="min-w-0">
      <p className="text-base font-medium text-fg">{label}</p>
      <p className="text-sm text-fg-2">{desc}</p>
    </div>
    <div className="shrink-0">{action}</div>
  </li>
);

const PrimaryBtn = ({ children, ...props }) => (
  <button
    type="button"
    className="rounded-full bg-cta px-4 py-2 text-sm font-medium text-cta-fg hover:brightness-[1.06] disabled:opacity-50"
    {...props}
  >
    {children}
  </button>
);

const PrimaryLink = ({ to, state, children }) => (
  <Link
    to={to}
    state={state}
    className="inline-block rounded-full bg-cta px-4 py-2 text-sm font-medium text-cta-fg hover:brightness-[1.06]"
  >
    {children}
  </Link>
);

const QuietLink = ({ to, children }) => (
  <Link to={to} className="text-sm font-medium text-fg-2">
    {children}
  </Link>
);

const Connected = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-good-strong/50 px-3 py-1.5 text-sm font-medium text-good-strong dark:text-good">
    Connected
  </span>
);

// Signed in: live status per provider and a real Connect button.
const SignedInDevices = () => {
  const { fitbitStatus, loading: loadingFitbit } = useFitbitStatus();
  const oura = useDeviceStatus('oura');
  const withings = useDeviceStatus('withings');
  const whoop = useDeviceStatus('whoop');
  const { connect, busy } = useDeviceConnect();

  const status = {
    fitbit: { connected: !!fitbitStatus?.connected, configured: true, loading: loadingFitbit },
    oura: { ...oura.deviceStatus, loading: oura.loading },
    withings: { ...withings.deviceStatus, loading: withings.loading },
    whoop: { ...whoop.deviceStatus, loading: whoop.loading },
  };

  const action = (key) => {
    const s = status[key];
    if (s.loading) return <span className="text-sm text-fg-3">Checking…</span>;
    if (s.connected) return <Connected />;
    if (s.configured === false) {
      return <QuietLink to="/me">Not switched on yet — ask for it</QuietLink>;
    }
    return (
      <PrimaryBtn onClick={() => connect(key)} disabled={busy === key}>
        {busy === key ? 'Opening…' : 'Connect'}
      </PrimaryBtn>
    );
  };

  const anyConnected = Object.values(status).some((s) => s.connected);

  return (
    <>
      <ul className="space-y-3">
        {DEVICES.map((d) => (
          <Row key={d.key} label={d.label} desc={d.desc} action={action(d.key)} />
        ))}
        <Row
          label="Android (Health Connect)"
          desc="Garmin, Samsung, Pixel Watch and anything else that writes to Health Connect — through our Android app"
          action={
            healthConnectSupported()
              ? <QuietLink to="/me">Set up in Me</QuietLink>
              : <QuietLink to="/me">Get the app in Me</QuietLink>
          }
        />
      </ul>
      {anyConnected && (
        <p className="mt-4 text-base text-fg-2">
          You’re connected. <QuietLink to="/home">See what’s moving →</QuietLink>
        </p>
      )}
    </>
  );
};

// Signed out: the same list, every action a sign-up that returns here.
const SignedOutDevices = ({ from }) => (
  <>
    <ul className="space-y-3">
      {DEVICES.map((d) => (
        <Row
          key={d.key}
          label={d.label}
          desc={d.desc}
          action={<PrimaryLink to="/register" state={{ from }}>Sign up to connect</PrimaryLink>}
        />
      ))}
      <Row
        label="Android (Health Connect)"
        desc="Garmin, Samsung, Pixel Watch and anything else that writes to Health Connect — through our Android app"
        action={<PrimaryLink to="/register" state={{ from }}>Sign up to connect</PrimaryLink>}
      />
    </ul>
    <p className="mt-4 text-base text-fg-2">
      Already have an account?{' '}
      <Link to="/login" state={{ from }} className="font-medium text-fg-2">
        Sign in
      </Link>
    </p>
  </>
);

const StartPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const from = { pathname: '/start', search: location.search };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-12">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-fg">
          Connect a device
        </h1>
        <p className="mt-2 text-base sm:text-lg text-fg-2">
          Vegetablah watches your health data and shows you what’s moving it. Connect the
          wearable you already own and we start reading — nothing to log, nothing to set up.
        </p>
      </div>

      <section aria-label="Devices" className="mb-10">
        {user ? <SignedInDevices /> : <SignedOutDevices from={from} />}
      </section>

      <section aria-label="What happens next" className="mb-10">
        <h2 className="text-xl font-medium text-fg">What happens next</h2>
        <ol className="mt-4 space-y-4">
          {STEPS.map((s) => (
            <li key={s.n} className="flex gap-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full 0/15 text-base font-medium text-fg-2">
                {s.n}
              </span>
              <div>
                <p className="text-base font-medium text-fg">{s.title}</p>
                <p className="text-sm text-fg-2">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-5 text-sm text-fg-2">
          Later, add what you take and see what moved after you started — things that changed
          alongside it, never things it caused.
        </p>
      </section>

      <p className="text-base text-fg-2">
        No wearable?{' '}
        <Link to={withSearch('/quiz', location.search)} className="font-medium text-fg-2">
          Start with the supplement quiz →
        </Link>
      </p>
    </div>
  );
};

export default StartPage;
